/**
 * The rule governing whether intake can be submitted.
 *
 * Two things decide it, over two different sets of files. Only the required
 * slots have to hold anything: without the invention disclosure there is no
 * invention to assess, and everything else on the page sharpens an assessment
 * rather than making one possible. But every file an Intake slot takes is read,
 * so each one has to have been read successfully, prior art and supporting
 * material included, even though supplying them was the attorney's choice. The
 * elements pass starts on submit and runs once, so a file still being read then
 * is never part of it.
 *
 * The slots are the ones the API serves, so the rule is built from them.
 */

import type { FileResponse } from "@patent-studio/api-types";
import type { IntakeComponentSpec } from "@/lib/intake-components";
import { isFileSettled } from "@/lib/file-status";
import { plural } from "@/lib/plural";
import type { FooterCondition } from "../layout/StageFooter";

/** Where the advance stands. Only `ready` opens the control. */
export type SubmitState = "missing" | "unreadable" | "reading" | "ready";

type Files = readonly FileResponse[] | undefined;

/** The element id of a component's slot on the intake page. */
export function intakeSlotId(spec: IntakeComponentSpec): string {
  return `intake-slot-${spec.fileType}`;
}

/** The element id of a held file's card on the intake page. */
export function intakeFileId(fileId: string): string {
  return `intake-file-${fileId}`;
}

function formatNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

const COUNT_WORDS = ["No", "One", "Two", "Three", "Four", "Five"];

function countWord(n: number): string {
  return COUNT_WORDS[n] ?? String(n);
}

function condition(
  id: string,
  first_id: string | undefined,
  label: string,
): FooterCondition {
  return first_id === undefined
    ? { id, outstanding: false, first_id: null, label }
    : { id, outstanding: true, first_id, label };
}

/**
 * The rule over the slots the API serves: what the gate bar says, and whether
 * it opens.
 */
export function intakeRule(specs: readonly IntakeComponentSpec[]) {
  const mandatory = specs.filter((c) => c.required);
  const readTypes = new Set<string>(specs.map((c) => c.fileType));

  const gating = (files: Files) =>
    (files ?? []).filter((f) => readTypes.has(f.file_type));

  /**
   * The files a component counts as holding. A held file is kept but never
   * read, so it yields no text and cannot be what a required component asks for.
   */
  const countIn = (files: Files, fileType: string) =>
    (files ?? []).filter(
      (f) => f.file_type === fileType && f.processing_status !== "held",
    ).length;

  const missingMandatory = (files: Files): string[] =>
    mandatory
      .filter((c) => countIn(files, c.fileType) === 0)
      .map((c) => c.title);

  const readingFiles = (files: Files) =>
    gating(files).filter((f) => !isFileSettled(f.processing_status));

  const failedFiles = (files: Files) =>
    gating(files).filter((f) => f.processing_status.endsWith("_failed"));

  /** Gating files the worker has not finished with. */
  const stillReading = (files: Files) =>
    readingFiles(files).map((f) => f.original_filename);

  /**
   * Files read at intake that the worker could not read.
   *
   * One failure is enough to block, and being optional is no defence: the
   * attorney chose to upload it, and an assessment that quietly skipped it
   * answers a narrower question than they asked while looking exactly like the
   * one they asked.
   */
  const unreadable = (files: Files) =>
    failedFiles(files).map((f) => f.original_filename);

  const submitState = (files: Files): SubmitState => {
    if (missingMandatory(files).length > 0) return "missing";
    // A failure outranks a file still being read: waiting cannot fix it.
    if (unreadable(files).length > 0) return "unreadable";
    if (stillReading(files).length > 0) return "reading";
    return "ready";
  };

  /** The disclosure documents the next stage reads, for the ready sentence. */
  const disclosureCount = (files: Files) =>
    mandatory.reduce((sum, c) => sum + countIn(files, c.fileType), 0);

  return {
    missingMandatory,
    stillReading,
    unreadable,
    submitState,

    /**
     * Whether the control accepts a click.
     *
     * Nothing in flight may be outstanding. Letting the click stand in for the
     * wait meant a removal could land between the two and send a half-changed
     * draft on.
     */
    canSubmit: (files: Files) => submitState(files) === "ready",

    /**
     * The files the gate waits on, as the footer's conditions: each one that
     * holds, named with its count and pointing at the first file card it is
     * waiting on. A missing disclosure is said by the bar's own heading, so
     * it is not listed again.
     */
    advanceConditions: (files: Files): FooterCondition[] => {
      const firstFailed = failedFiles(files).at(0);
      const firstReading = readingFiles(files).at(0);
      return [
        condition(
          "readable",
          firstFailed && intakeFileId(firstFailed.file_id),
          `${plural(failedFiles(files).length, "file")} could not be read`,
        ),
        condition(
          "reading",
          firstReading && intakeFileId(firstReading.file_id),
          `Reading ${plural(readingFiles(files).length, "file")}…`,
        ),
      ];
    },

    /**
     * The bold line on the gate bar. Names exactly which required slots are
     * empty, so the attorney need not work out which slot holds them up.
     */
    advanceHeading: (files: Files): string => {
      const failed = unreadable(files);
      switch (submitState(files)) {
        case "unreadable":
          return `${countWord(failed.length)} file${failed.length === 1 ? "" : "s"} could not be read`;
        case "reading":
          return "Reading your documents";
        case "missing":
          return `Add the ${formatNames(missingMandatory(files)).toLowerCase()} to continue`;
        default:
          return "Materials ready";
      }
    },

    advanceDetail: (files: Files): string => {
      const documents = disclosureCount(files);
      switch (submitState(files)) {
        case "unreadable":
          return `Remove or replace ${formatNames(unreadable(files))} to continue.`;
        case "missing":
          return "The disclosure is required. The figures and, after them, Invention are built from it.";
        case "reading":
          return `Reading ${formatNames(stillReading(files))}.`;
        default:
          return `Invention reads ${plural(documents, "disclosure document")} and builds the elements of the invention from ${documents === 1 ? "it" : "them"}.`;
      }
    },
  };
}

export type IntakeRule = ReturnType<typeof intakeRule>;
